import { AsyncPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  HostBinding,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { AppService } from '@nk/access-block';
import { formatOrgNr, OwnershipData } from '@nk/company-block';
import { ReportTitlePageComponent, ReportTitlePageData, SpinnerComponent } from '@nk/ui-lib';
import {
  DateFormats,
  LOADING_STATE_HANDLER_SERVICE_TOKEN,
  LoadingStateService,
  titleCaseCompanyName,
} from '@nk/utils-lib';
import { combineLatest, Observable } from 'rxjs';
import { MonitoringOriginEntityService } from '../../services/monitoring-origin-entity.service';
import { ScreeningInputParamsService } from '../../services/screening-input-params.service';
import { MonitoringConnectionCardListComponent } from '../monitoring-connection-card-list/monitoring-connection-card-list.component';
import { ScreeningCaseCardListComponent } from '../screening-case-card-list/screening-case-card-list.component';
import { ScreeningReportCompanyBaseInfoComponent } from '../screening-report-company-base-info/screening-report-company-base-info.component';
import { ScreeningReportCompanyLegalRightComponent } from '../screening-report-company-legal-right/screening-report-company-legal-right.component';
import { ScreeningReportCompanyOwnershipComponent } from '../screening-report-company-ownership/screening-report-company-ownership.component';
import { ScreeningReportCompanyRolesComponent } from '../screening-report-company-roles/screening-report-company-roles.component';

@Component({
  selector: 'nk-screening-report-view',
  imports: [
    AsyncPipe,
    SpinnerComponent,
    ScreeningReportCompanyBaseInfoComponent,
    ReportTitlePageComponent,
    MonitoringConnectionCardListComponent,
    ScreeningReportCompanyLegalRightComponent,
    ScreeningReportCompanyOwnershipComponent,
    ScreeningReportCompanyRolesComponent,
    ScreeningCaseCardListComponent,
  ],
  templateUrl: './screening-report-view.component.html',
  styleUrls: ['./screening-report-view.component.scss'],
  providers: [DatePipe, { provide: LOADING_STATE_HANDLER_SERVICE_TOKEN, useClass: LoadingStateService }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningReportViewComponent implements OnInit {
  public screeningEntitiesInputParams = computed(() => this._screeningInputParamsService.screeningInputParams());
  public isReportLoading$!: Observable<boolean>;
  public screeningReportOwnershipData$!: Observable<OwnershipData | null>;
  public reportTitlePageData = signal<ReportTitlePageData | undefined>(undefined);
  @HostBinding('@.disabled')
  public animationsDisabled = true;
  private _appService = inject(AppService);
  private _screeningInputParamsService = inject(ScreeningInputParamsService);
  private _monitoringOriginEntityService = inject(MonitoringOriginEntityService);
  private _activatedRoute = inject(ActivatedRoute);
  private _translateService = inject(TranslateService);
  private _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });
  private _datePipe = inject(DatePipe);
  private _destroy = inject(DestroyRef);
  private _monitoringOriginEntityLoadingId = computed(() => `${this.constructor.name}_monitoringOriginEntity`);

  public ngOnInit(): void {
    if (this._reportLoadingStateService) {
      this._reportLoadingStateService.setLoadingState(this._monitoringOriginEntityLoadingId(), true);
      this.isReportLoading$ = this._reportLoadingStateService.isReportLoading$;
    }
    const screeningConfigUuid = this._activatedRoute.snapshot.params['screeningConfigUuid'];
    const monitoringOriginEntityUuid = this._activatedRoute.snapshot.params['monitoringOriginEntityUuid'];

    combineLatest([
      this._monitoringOriginEntityService.monitoringOriginEntityCompositeCache.get(
        screeningConfigUuid,
        monitoringOriginEntityUuid,
      ),
      this._appService.identityDataCache.get(),
    ])
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe(([monitoringOriginEntityComposite, identity]) => {
        const company = monitoringOriginEntityComposite.company;
        if (!company) {
          return;
        }
        this._screeningInputParamsService.triggerScreeningEntitiesRefresh({
          screeningConfigUuid,
          monitoringOriginEntityUuid,
          companyUuid: company.uuid,
          customerPersonUuid: undefined,
          appMembershipUuid: identity.app_membership.uuid,
        });
        const today: number = Date.now();
        this.reportTitlePageData.set({
          headerText: extractTranslation('screening.screening_overrides.export.pep_and_sanction_status_title'),
          title: titleCaseCompanyName(company.name) || '',
          subtitle: formatOrgNr(company.org_nr, company.org_nr_schema),
          description: '',
          footerText: this._translateService.instant(extractTranslation('report.exported_by'), {
            user: identity.app_user.name,
            date: this._datePipe.transform(today, DateFormats.DateAndTime),
          }),
        });
        if (this._reportLoadingStateService) {
          this._reportLoadingStateService.setLoadingState(this._monitoringOriginEntityLoadingId(), false);
        }
      });
  }
}
